import {
  ContextMenu,
  ContextMenuChildrenProps,
  ContextMenuContentProps,
  ContextMenuProps,
} from "@blueprintjs/core";
import { LongPressProps, useLongPress } from "@react-aria/interactions";
import { LongPressEvent } from "@react-types/shared";
import React, { type JSX, useCallback, useMemo } from "react";
import styled from "styled-components";

import { useId } from "../hooks/useUniqueId";

export interface HexContextMenuProps<
  TTargetElement extends HTMLElement = HTMLElement,
> extends Omit<ContextMenuProps, "children" | "content"> {
  /**
   * The target of the context menu. Pass a `ReactNode` when the content is not
   * normally interactive. Pass a render function when the content is normally
   * interactive, or to use component state to render the target.
   */
  children?:
    | React.ReactNode
    | ((props: HexContextMenuChildrenProps<TTargetElement>) => React.ReactNode);
  /**
   * The content of the context menu. Optionally specify a render function to use
   * component state to render the content.
   */
  content?:
    | JSX.Element
    | ((props: HexContextMenuContentProps<TTargetElement>) => JSX.Element);
  longPressAccessibilityDescription?: LongPressProps["accessibilityDescription"];
  longPressThreshold?: LongPressProps["threshold"];
  onLongPress?: LongPressProps["onLongPress"];
  onLongPressEnd?: LongPressProps["onLongPressEnd"];
  onLongPressStart?: LongPressProps["onLongPressStart"];
}

/** Render props for the content of a context menu. */
export interface HexContextMenuContentProps<
  TTargetElement extends HTMLElement = HTMLElement,
> extends Omit<ContextMenuContentProps, "mouseEvent"> {
  /** The context menu click event. If isOpen is false, this will be undefined. */
  mouseEvent: React.MouseEvent<TTargetElement> | undefined;
  /** (only defined when content is a render function) */
  id: string | undefined;
}

/** Render props for the children of a context menu when passed as a function. */
export interface HexContextMenuChildrenProps<
  TTargetElement extends HTMLElement = HTMLElement,
> {
  /** Render props for the content of a context menu. */
  contentProps: HexContextMenuContentProps<TTargetElement>;
  /** Render props for the target of a context menu. */
  targetProps: Omit<ReactAriaLongPressTargetProps, "onKeyDown"> & {
    className: ContextMenuChildrenProps["className"];
    ref: ContextMenuChildrenProps["ref"];
    /** Context menu handler which implements the custom context menu interaction */
    onContextMenu?: React.MouseEventHandler<TTargetElement>;
    /** Keyboard handler which implements the custom context menu interaction */
    onKeyDownCapture?: React.KeyboardEventHandler<TTargetElement>;
    "aria-haspopup": "true";
    "aria-expanded": boolean;
    /** (only defined when content is a render function) */
    "aria-controls": string | undefined;
  };
}

/**
 * Displays a list of actions when right-clicking on a target element.
 *
 * ---
 *
 * @example
 * When the target is not normally interactive, you can
 * 1. pass children as a `ReactNode`
 * 2. optionally, control the `tagName`
 * ```tsx
 * <HexContextMenu tagName="span">
 *   Right-click me
 * </HexContextMenu>
 * ```
 *
 * ---
 *
 * @example
 * When the target is normally interactive, you **should**
 * 1. pass children as a render function
 * 2. not pass a custom `tagName
 * ```tsx
 * <HexContextMenu>
 *   {({ targetProps }) => (
 *     <HexButton {...targetProps}>Right-click me</HexButton>
 *   )}
 * </HexContextMenu>
 * ```
 *
 * ---
 *
 * Extends the `ContextMenu` component from `@blueprintjs/core`.
 * @see https://blueprintjs.com/docs/#core/components/context-menu
 *
 * ---
 *
 * Implements the ARIA Menu Button pattern.
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/
 */
export const HexContextMenu = React.memo(
  React.forwardRef<HTMLDivElement, HexContextMenuProps>(function HexContextMenu(
    {
      children: childrenProp,
      content = null,
      disabled,
      longPressAccessibilityDescription = "Long press to open context menu",
      longPressThreshold,
      onLongPress,
      onLongPressEnd,
      onLongPressStart,
      tagName = "div",
      ...props
    },
    ref,
  ) {
    // since we don't wrap an element around the content by default, we can't
    // guarantee there will be an element with this value, in which case the
    // target shouldn't set "aria-controls". only set menu id if content is a
    // render function
    const menuId_ = useId();
    const menuId = typeof content === "function" ? menuId_ : undefined;

    const handleLongPress = useCallback(
      (event: LongPressEvent) => {
        onLongPress?.(event);
        // determine context menu placement
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const clientX = rect.left + event.x;
        const clientY = rect.top + event.y;
        // simulate right-click on the element
        const rightClickEvent = new MouseEvent("contextmenu", {
          bubbles: true,
          cancelable: true,
          clientX,
          clientY,
        });
        event.target.dispatchEvent(rightClickEvent);
      },
      [onLongPress],
    );

    const {
      // React Aria calls `preventDefault` too aggressively on the `onKeyDown`
      // event such that even though we _only_ want to listen to long-presses, it
      // prevents us from listening to action keys like Enter or Space. (Specifically,
      // this is because `useLongPress` extends `usePress`. If we used `usePress`
      // on all interactive elements in addition, then we wouldn't run into problems
      // since the semantics would be consistent.) Since long-presses are only pointer
      // events, not keyboard events, we can just remove `onKeyDown`.
      longPressProps: { onKeyDown: _onKeyDown, ...longPressProps },
    } = useLongPress({
      isDisabled: disabled,
      threshold: longPressThreshold,
      onLongPress: handleLongPress,
      onLongPressEnd,
      onLongPressStart,
      accessibilityDescription: longPressAccessibilityDescription,
    }) as Omit<ReactAriaUseLongPressResult, "onKeyDown">;

    // while this should be just onKeyDown, we need to use onKeyDownCapture since
    // 1. We should respect the consumer ability to prevent this default behavior
    // 2. Blueprint's Button does not call consumer handlers first and calls
    //    preventDefault on Enter key
    // attaching a handler during the capture phase is a hacky way of injecting
    // this logic
    const handleKeyDownCapture = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.defaultPrevented || disabled) return;

        // MacOS introduced keyboard support for context menus in Sequoia 15.1
        // with Ctrl+Enter. However it requires application support as well.
        // Windows claims solid support for Shift+F10. To make it simple,
        // support Ctrl+Enter everywhere and let Windows users have their
        // native alternative.
        if (event.ctrlKey && event.key === "Enter") {
          // prevent typical activation from Enter key
          event.preventDefault();
          // determine context menu placement
          const rect = (event.target as HTMLElement).getBoundingClientRect();
          const clientX = rect.right - 24;
          const clientY = rect.top + rect.height / 2;
          // simulate right-click on the element
          const rightClickEvent = new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            clientX,
            clientY,
          });
          event.target.dispatchEvent(rightClickEvent);
        }
      },
      [disabled],
    );

    const injectHexContentProps = useCallback(
      (bpContentProps: ContextMenuContentProps): HexContextMenuContentProps => {
        return {
          ...bpContentProps,
          id: menuId,
        };
      },
      [menuId],
    );

    const injectHexTargetProps = useCallback(
      (
        bpChildrenProps: ContextMenuChildrenProps,
        hasNonInteractiveChildren?: boolean,
      ): HexContextMenuChildrenProps["targetProps"] => {
        return {
          className: bpChildrenProps.className,
          ref: bpChildrenProps.ref,
          onContextMenu: bpChildrenProps.onContextMenu,
          onKeyDownCapture: handleKeyDownCapture,
          "aria-haspopup": "true",
          "aria-expanded": bpChildrenProps.contentProps.isOpen,
          "aria-controls": menuId,
          ...(hasNonInteractiveChildren ? { tabIndex: 0, role: "button" } : {}),
          ...longPressProps,
        };
      },
      [handleKeyDownCapture, longPressProps, menuId],
    );

    const bpChildren = useMemo<ContextMenuProps["children"]>(() => {
      if (childrenProp === undefined) {
        return null;
      } else if (typeof childrenProp === "function") {
        // eslint-disable-next-line react/display-name -- not valuable
        return (
          bpChildrenProps: ContextMenuChildrenProps,
        ): React.ReactElement<any> => (
          <>
            {childrenProp({
              contentProps: injectHexContentProps(bpChildrenProps.contentProps),
              targetProps: injectHexTargetProps(bpChildrenProps),
            })}
            {bpChildrenProps.popover}
          </>
        );
      } else {
        return childrenProp;
      }
    }, [childrenProp, injectHexContentProps, injectHexTargetProps]);

    const bpContent = useMemo(() => {
      if (typeof content === "function") {
        return (bpContentProps: ContextMenuContentProps) =>
          content({ ...bpContentProps, id: menuId });
      } else {
        return content;
      }
    }, [content, menuId]);

    return (
      <StyledContextMenu
        ref={ref}
        content={bpContent ?? undefined}
        disabled={disabled}
        onKeyDownCapture={handleKeyDownCapture}
        {...props}
      >
        {(bpChildrenProps) =>
          typeof bpChildren === "function" ? (
            bpChildren(bpChildrenProps)
          ) : (
            <>
              {React.createElement(
                tagName as keyof JSX.IntrinsicElements,
                injectHexTargetProps(bpChildrenProps, true),
                bpChildren,
              )}
              {bpChildrenProps.popover}
            </>
          )
        }
      </StyledContextMenu>
    );
  }),
);

const StyledContextMenu = styled(ContextMenu)`
  /* these styles passed to the children */
  &[aria-expanded="true"] {
    background-color: ${({ theme }) => theme.activeColor};
  }
`;

type ReactAriaUseLongPressResult = {
  longPressProps: ReactAriaLongPressTargetProps;
};

interface ReactAriaLongPressTargetProps {
  "aria-describedby": string | undefined;
  "data-react-aria-pressable": boolean;
  onClick: React.MouseEventHandler;
  onKeyDown: React.KeyboardEventHandler;
  onMouseDown: React.MouseEventHandler;
  onPointerDown: React.PointerEventHandler;
  onPointerEnter: React.PointerEventHandler;
  onPointerLeave: React.PointerEventHandler;
  onPointerUp: React.PointerEventHandler;
}
